import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { TableLayerListInfoComponent } from '@connect-services/client/components/add-layer-list-info/table-layer-list-info/table-layer-list-info.component';
import {
    AddLayerSelectionType,
    AddLayerService,
    ServiceCatalogResolutionError,
} from '@connect-services/client/services/add-layer.service';
import { CustomColumn } from '@connect-services/src/lib/components/table/table.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LoadingComponent, RessourcesModule, TableLayerListInfoComponent],
    selector: 'app-add-layer-list-info',
    styleUrl: './add-layer-list-info.component.scss',
    templateUrl: './add-layer-list-info.component.html',
})
export class AddLayerListInfoComponent {
    public readonly hasFieldInterventions = input.required<boolean>();
    public readonly layerType = input.required<AddLayerSelectionType>();
    public readonly unresolvedGeomServiceCatalogs = computed<ServiceCatalogResolutionError[]>(() =>
        this.addLayerService
            .serviceCatalogResolutionErrors()
            .filter((error: ServiceCatalogResolutionError) => !!error.hasGeom),
    );
    public readonly unresolvedNoGeomServiceCatalogs = computed<ServiceCatalogResolutionError[]>(() =>
        this.addLayerService
            .serviceCatalogResolutionErrors()
            .filter((error: ServiceCatalogResolutionError) => !error.hasGeom),
    );

    protected readonly addLayerService = inject(AddLayerService);
    protected readonly allColumns = computed<CustomColumn[]>(() => {
        return this.getColumnsByLayerType();
    });
    protected readonly isInterstandardFolderSelected = computed(() => {
        return (
            this.layerType() === AddLayerSelectionType.INTERSTANDARD &&
            !!this.addLayerService.selectedFirstInterstandardDepositFolder() &&
            !!this.addLayerService.selectedSecondInterstandardDepositFolder()
        );
    });
    protected readonly noGeomColumns = computed<CustomColumn[]>(() => {
        return this.allColumns().filter((column: CustomColumn) => !this.geomOnlyColumnKeys.has(column.key));
    });
    protected readonly Object = Object;
    protected readonly shouldShowFieldInterventionColumn = computed(() => {
        return this.addLayerService.canShowFieldInterventionColumn(this.hasFieldInterventions());
    });

    private readonly baseColumns: CustomColumn[] = [
        { key: 'name', label: 'name', sortable: false },
        { key: 'visibility', label: 'visibility', sortable: false },
        { key: 'order', label: 'order', sortable: false },
    ];
    private readonly fieldInterventionContextColumn: CustomColumn = {
        key: 'fieldInterventionContext',
        label: 'fieldIntervention',
        sortable: false,
    };
    private readonly geomOnlyColumnKeys: ReadonlySet<string> = new Set([
        'visibility',
        'order',
        'fieldInterventionContext',
    ]);
    private readonly identifierColumn: CustomColumn = {
        key: 'identifier',
        label: 'identifier',
        sortable: false,
    };
    private readonly interstandardLeadingColumns: CustomColumn[] = [
        { key: 'firstData', label: 'firstData', sortable: false },
        { key: 'secondData', label: 'secondData', sortable: false },
    ];
    private readonly tableLeadingColumns: CustomColumn[] = [
        { key: 'tables', label: 'tables', sortable: false },
        { key: 'dataVersion', label: 'dataVersion', sortable: false },
        { key: 'lock', label: '', sortable: false },
    ];

    private getColumnsByLayerType(): CustomColumn[] {
        switch (this.layerType()) {
            case AddLayerSelectionType.REFERENCE_DATA:
            case AddLayerSelectionType.TABLE:
                return this.getTableGeomColumns();

            case AddLayerSelectionType.INTERSTANDARD:
                return this.getInterstandardGeomColumns();

            default:
                return this.baseColumns;
        }
    }

    private getInterstandardGeomColumns(): CustomColumn[] {
        return [...this.interstandardLeadingColumns, ...this.baseColumns, this.identifierColumn];
    }

    private getTableGeomColumns(): CustomColumn[] {
        return [
            ...this.tableLeadingColumns,
            ...this.baseColumns,
            ...(this.shouldShowFieldInterventionColumn() ? [this.fieldInterventionContextColumn] : []),
            this.identifierColumn,
        ];
    }
}
